import React, { createContext, useContext } from 'react';

import { Control, FieldErrors, FieldValues } from 'react-hook-form';

interface FormContextProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
}

const FormContext = createContext<FormContextProps<FieldValues> | null>(null);

type FormProviderProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  children: React.ReactNode;
};

export function FormProvider<TFieldValues extends FieldValues>({
  control,
  errors,
  children,
}: FormProviderProps<TFieldValues>) {
  return (
    <FormContext.Provider
      value={{
        control: control as unknown as Control<FieldValues>,
        errors: errors as unknown as FieldErrors<FieldValues>,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}

export function useFormContext<TFieldValues extends FieldValues = FieldValues>() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context as unknown as FormContextProps<TFieldValues>;
}

export function getFieldErrorMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return undefined;
}
